// CLASSIC LOOK (v1.70): pre-Floodlight copy from ea44aec, used by
// screens/classic/CommunityScreen.jsx. Keep behaviour in step with
// components/community/MemberCard.jsx (the Floodlight version).
import { memo, useMemo, useRef, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  StyleSheet,
  Animated,
  Dimensions,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

import { theme } from "../../../constants/theme";

// ─── Layout constants ─────────────────────────────────────────────────────────

const CARD_WIDTH       = 300;
const BANNER_HEIGHT    = 68;
const AVATAR_SIZE      = 60;
const AVATAR_OVERLAP   = AVATAR_SIZE / 2;   // how far avatar hangs below banner
const EST_CARD_HEIGHT  = 310;               // used for viewport clamping before layout

// ─── Helpers ─────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "#A259FF", "#4ade80", "#f87171", "#fbbf24",
  "#60a5fa", "#f472b6", "#34d399", "#fb923c",
];

function avatarColor(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function initials(str = "") {
  const parts = str.replace(/\d+$/, "").match(/[A-Z][a-z]*/g) || [str];
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase() || str.slice(0, 2).toUpperCase();
}

function formatJoinedDate(raw) {
  if (!raw) return null;
  try {
    const d = raw.toDate ? raw.toDate() : new Date(raw);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return null;
  }
}

const STATUS_COLOR = {
  online:  "#23A55A",
  idle:    "#F0B232",
  dnd:     "#F23F43",
  offline: "#80848E",
};

const STATUS_LABEL = {
  online:  "Online",
  idle:    "Away",
  dnd:     "Do Not Disturb",
  offline: "Offline",
};

// ─── Position calculator ──────────────────────────────────────────────────────

function calcCardPosition(position) {
  if (!position) return { top: 80, left: 80 };

  const { width: sw, height: sh } = Dimensions.get("window");
  const { x, y, width: rw } = position;

  // Prefer to the left of the row; fall back to the right
  let left = x - CARD_WIDTH - 10;
  if (left < 8) left = x + rw + 10;

  // Clamp to screen edges
  left = Math.max(8, Math.min(left, sw - CARD_WIDTH - 8));
  let top  = Math.max(8, Math.min(y - 12, sh - EST_CARD_HEIGHT - 8));

  return { top, left };
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * MemberCard
 *
 * Discord-style member profile popup anchored near the clicked row.
 *
 * Props:
 *   member    {object|null}  Member data (null = hidden)
 *   position  {{ x, y, width, height }|null}  measureInWindow result of the row
 *   onClose   {function}
 */
const MemberCard = memo(function MemberCard({ member, position, onClose }) {
  const visible = !!member;

  // ── Animation refs ──────────────────────────────────────────────────────────
  const opacity    = useRef(new Animated.Value(0)).current;
  const scale      = useRef(new Animated.Value(0.88)).current;
  const translateY = useRef(new Animated.Value(6)).current;

  // Re-animate whenever the card opens or the target member changes
  useEffect(() => {
    if (!visible) return;

    // Reset before each open so switching members replays the spring
    opacity.setValue(0);
    scale.setValue(0.88);
    translateY.setValue(6);

    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 130,
        useNativeDriver: true,
      }),
      Animated.spring(scale, {
        toValue: 1,
        tension: 260,
        friction: 18,
        useNativeDriver: true,
      }),
      Animated.spring(translateY, {
        toValue: 0,
        tension: 260,
        friction: 18,
        useNativeDriver: true,
      }),
    ]).start();
  }, [visible, member?.id]);

  // ── Derived display values ──────────────────────────────────────────────────
  const aColor      = useMemo(() => member ? avatarColor(member.name) : theme.colors.brandPrimary, [member?.name]);
  const abbr        = useMemo(() => member ? initials(member.name) : "", [member?.name]);
  const joined      = useMemo(() => member ? formatJoinedDate(member.joinedAt) : null, [member?.joinedAt]);
  const statusColor = STATUS_COLOR[member?.status] ?? STATUS_COLOR.offline;
  const statusLabel = STATUS_LABEL[member?.status] ?? "Offline";
  const showHandle  = !!(member?.username && member.username !== member.name);
  const cardPos     = useMemo(() => calcCardPosition(position), [position]);

  if (!member) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent={Platform.OS === "android"}
    >
      {/* Invisible full-screen dismiss layer */}
      <TouchableOpacity
        style={StyleSheet.absoluteFillObject}
        activeOpacity={1}
        onPress={onClose}
        accessibilityLabel="Close member card"
      />

      {/* Animated card — rendered on top of the dismiss layer */}
      <Animated.View
        style={[
          styles.card,
          cardPos,
          { opacity, transform: [{ scale }, { translateY }] },
        ]}
        // Claim touches so they don't fall through to the dismiss layer
        onStartShouldSetResponder={() => true}
      >
        {/* ── Banner ─────────────────────────────────────────────── */}
        <View style={[styles.banner, { backgroundColor: aColor }]}>
          {/* Subtle dark-to-transparent bottom fade */}
          <View style={styles.bannerFade} pointerEvents="none" />
          <TouchableOpacity style={styles.closeBtn} onPress={onClose} hitSlop={10} accessibilityRole="button" accessibilityLabel="Close">
            <Ionicons name="close" size={15} color="rgba(255,255,255,0.8)" />
          </TouchableOpacity>
        </View>

        {/* ── Avatar — straddles banner / body boundary ─────────── */}
        <View style={styles.avatarRow}>
          <View style={[styles.avatarOuter, { borderColor: theme.colors.backgroundCard }]}>
            <View style={[styles.avatar, { backgroundColor: aColor }]}>
              <Text style={styles.avatarText}>{abbr}</Text>
            </View>
            {/* Status dot */}
            <View
              style={[
                styles.statusDot,
                {
                  backgroundColor: statusColor,
                  borderColor: theme.colors.backgroundCard,
                },
              ]}
            />
          </View>
        </View>

        {/* ── Body ───────────────────────────────────────────────── */}
        <View style={styles.body}>
          {/* Name + handle */}
          <Text style={styles.displayName} numberOfLines={1}>
            {member.name}
          </Text>
          {showHandle && (
            <Text style={styles.handle} numberOfLines={1}>
              @{member.username}
            </Text>
          )}

          {/* Status label */}
          <View style={styles.statusRow}>
            <View style={[styles.statusPip, { backgroundColor: statusColor }]} />
            <Text style={[styles.statusText, { color: statusColor }]}>
              {statusLabel}
            </Text>
          </View>

          <View style={styles.divider} />

          {/* Member since */}
          <View style={styles.infoRow}>
            <Ionicons
              name="calendar-outline"
              size={13}
              color={theme.colors.textTertiary}
              style={styles.infoIcon}
            />
            <View>
              <Text style={styles.infoLabel}>MEMBER SINCE</Text>
              <Text style={styles.infoValue}>{joined ?? "—"}</Text>
            </View>
          </View>

          {/* Subscription */}
          <View style={styles.infoRow}>
            <Ionicons
              name="diamond-outline"
              size={13}
              color={theme.colors.textTertiary}
              style={styles.infoIcon}
            />
            <View>
              <Text style={styles.infoLabel}>SUBSCRIPTION</Text>
              <View
                style={[
                  styles.planBadge,
                  { borderColor: member.planColor ?? theme.colors.borderStrong },
                ]}
              >
                <Text
                  style={[
                    styles.planBadgeText,
                    { color: member.planColor ?? theme.colors.textTertiary },
                  ]}
                >
                  {member.planName ?? "Free"}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Animated.View>
    </Modal>
  );
});

export default MemberCard;

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  // ── Card shell ────────────────────────────────────────────────────
  card: {
    position: "absolute",
    width: CARD_WIDTH,
    backgroundColor: theme.colors.backgroundCard,
    borderRadius: theme.radii.md,
    borderWidth: 1,
    borderColor: theme.colors.borderSoft,
    overflow: "hidden",
    ...theme.shadows.cardHover,
  },

  // ── Banner ────────────────────────────────────────────────────────
  banner: {
    height: BANNER_HEIGHT,
    justifyContent: "flex-start",
    alignItems: "flex-end",
    padding: 10,
  },
  bannerFade: {
    ...StyleSheet.absoluteFillObject,
    // On native, approximate with a gradient-less overlay
    backgroundColor: "rgba(0,0,0,0.08)",
  },
  closeBtn: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "rgba(0,0,0,0.25)",
    alignItems: "center",
    justifyContent: "center",
  },

  // ── Avatar straddling banner / body ───────────────────────────────
  avatarRow: {
    // Pull the avatar up to overlap the banner
    marginTop: -AVATAR_OVERLAP,
    paddingHorizontal: 14,
    marginBottom: 8,
  },
  avatarOuter: {
    width: AVATAR_SIZE + 6,
    height: AVATAR_SIZE + 6,
    borderRadius: (AVATAR_SIZE + 6) / 2,
    borderWidth: 3,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: {
    color: "#fff",
    fontSize: 22,
    fontWeight: "700",
  },
  statusDot: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2.5,
  },

  // ── Body ──────────────────────────────────────────────────────────
  body: {
    paddingHorizontal: 14,
    paddingBottom: 14,
  },
  displayName: {
    fontSize: 17,
    fontWeight: "700",
    color: theme.colors.textPrimary,
  },
  handle: {
    fontSize: 13,
    color: theme.colors.textTertiary,
    marginTop: 1,
  },
  statusRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    marginTop: 5,
  },
  statusPip: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 12,
    fontWeight: "500",
  },

  divider: {
    height: 1,
    backgroundColor: theme.colors.borderSoft,
    marginVertical: 12,
  },

  // ── Info rows ─────────────────────────────────────────────────────
  infoRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 10,
  },
  infoIcon: {
    marginTop: 1,
  },
  infoLabel: {
    fontSize: 10,
    fontWeight: "700",
    color: theme.colors.textTertiary,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 3,
  },
  infoValue: {
    fontSize: 13,
    color: theme.colors.textPrimary,
    fontWeight: "500",
  },

  // ── Plan badge ────────────────────────────────────────────────────
  planBadge: {
    alignSelf: "flex-start",
    paddingHorizontal: 9,
    paddingVertical: 3,
    borderRadius: theme.radii.pill,
    borderWidth: 1,
  },
  planBadgeText: {
    fontSize: 11,
    fontWeight: "700",
    letterSpacing: 0.3,
  },
});
